import { CheckOutlined } from '@ant-design/icons';
import { enumManagers } from '@chirp/enums';
import { Tag } from 'antd';
import pluralize from 'pluralize';
import React from 'react';

import { convertSecondsToLargestUnit } from '../../components/DurationField/helpers';
import { Color } from '../../components/EnumTag';
import ImageThumbnail from '../../components/ImageThumbnail';
import { Timetable } from '../../components/Timetable';
import {
  AccessPointAvailabilityFragment,
  AccessPointDetailsFragment,
  AccessPointType_enum,
  ImageThumbnailFragment as Image,
  PermissionScope_enum,
  PropertyLabelFragment,
} from '../../graphql/hasura/generated';
import { getAvailabilityDescription } from '../../utils';
import { PropertyModel } from '../Property/model';
import { UserModel } from '../User/model';

import { AccessPointTypeEnumTag } from './components/AccessPointTypeEnumTag';
import model from './model';

export const propertyColumn = model.createColumn<{ property: PropertyLabelFragment }>({
  filterOptions: {
    type: 'MODEL_SELECT',
    key: 'propertyId',
    model: PropertyModel,
  },
  enabled: ({ currentPermissionScope }) => (
    currentPermissionScope === PermissionScope_enum.GLOBAL ||
    currentPermissionScope === PermissionScope_enum.ORGANIZATION
  ),
  render: ({ property }) => PropertyModel.routes.renderRowLink(property),
});

export const nameColumn = model.createColumn<{ name: string }>({
  dataIndex: 'name',
});

export const imageColumn = model.createColumn<{ name: string, image?: Image | null }>({
  title: 'Image',
  sorter: false,
  render: ({ name, image }) => <ImageThumbnail title={name} image={image} />,
  renderString: false,
});

export const typeColumn = model.createColumn<{ type: AccessPointType_enum }>({
  title: 'Type',
  filterOptions: {
    type: 'ENUM',
    enumTable: 'AccessPointType',
  },
  render: ({ type }) => <AccessPointTypeEnumTag type={type} />,
  renderString: ({ type }) => enumManagers.AccessPointType.getLabel(type),
});

export const accessTimeoutColumn = model.createColumn<{ accessTimeout?: number | null }>({
  title: 'Access Timeout',
  dataIndex: 'accessTimeout',
  render: ({ accessTimeout }) => {
    if (typeof accessTimeout !== 'number') {
      return null;
    }

    const timeoutInSeconds = accessTimeout / 1000;

    return `${(timeoutInSeconds)} ${pluralize('second', timeoutInSeconds)}`;
  },
});

export const availabilityColumn = model.createColumn<AccessPointAvailabilityFragment>({
  title: 'Availability',
  filterOptions: {
    type: 'RADIO',
    key: 'availability',
    options: [
      {
        label: 'Available 24/7',
        queryFilters: {
          _and: [
            {
              timetable: { _is_null: true },
            },
            {
              _not: {
                accessPointRoles: {
                  schedule: { _is_null: false }
                },
              },
            }
          ],
        },
      },
      {
        label: 'Schedules Applied',
        queryFilters: {
          _or: [
            {
              timetable: { _is_null: false },
            },
            {
              accessPointRoles: {
                schedule: { _is_null: false }
              },
            },
          ],
        },
      },
    ]
  },
  render: ({ timetable, property }) => {
    const availability = getAvailabilityDescription(new Date(), timetable, property.timezone);

    if (availability === 'LOCKDOWN') {
      return (
        <Tag color={Color.Red} style={{ fontWeight: 'bold' }}>
          LOCKDOWN
        </Tag>
      );
    }

    return availability;
  },
});

export const spaceshipModeEnabledColumn = model.createColumn<{ spaceshipModeEnabled: boolean }>({
  title: 'Spaceship Mode Enabled',
  filterOptions: {
    type: 'RADIO',
    key: 'spaceshipModeEnabled',
  },
  render: ({ spaceshipModeEnabled }) => {
    return spaceshipModeEnabled ? <CheckOutlined /> : null;
  },
  renderString: ({ spaceshipModeEnabled }) => spaceshipModeEnabled ? 'Yes' : 'No',
});

export const proximityRestrictionColumn = model.createColumn<{ proximityRestriction: boolean }>({
  title: 'Proximity Restriction',
  filterOptions: {
    type: 'RADIO',
    key: 'proximityRestriction',
  },
  render: ({ proximityRestriction }) => {
    return proximityRestriction ? <CheckOutlined /> : null;
  },
  renderString: ({ proximityRestriction }) => proximityRestriction ? 'Yes' : 'No',
});

// Details Page columns

export const maxOccupancyColumn = model.createColumn<AccessPointDetailsFragment>({
  title: 'Max Occupancy',
  render: ({ maxOccupancy }) => maxOccupancy,
});

export const rexAccessTimeoutColumn = model.createColumn<{ rexAccessTimeout: number }>({
  title: 'REX Access Timeout',
  render: ({ rexAccessTimeout }) => {
    const timeoutInSeconds = rexAccessTimeout / 1000;

    return `${(timeoutInSeconds)} ${pluralize('second', timeoutInSeconds)}`;
  },
});

export const scheduleColumn = model.createColumn<{ timetable?: any }>({
  title: 'Schedule',
  render: ({ timetable }) => <Timetable timetable={timetable} groupByState />,
});

export const spaceshipModeEnabledDetailsColumn = model.createColumn<AccessPointDetailsFragment>({
  title: 'Spaceship Mode Enabled',
  render: ({ spaceshipModeEnabled }) => spaceshipModeEnabled ? 'Yes' : 'No',
});

export const proximityRestrictionDetailsColumn = model.createColumn<AccessPointDetailsFragment>({
  title: 'Proximity Restriction',
  render: ({ proximityRestriction }) => proximityRestriction ? 'Yes' : 'No',
});

export const dpsAlertDelayColumn = model.createColumn<AccessPointDetailsFragment>({
  title: 'DPS Alert Delay',
  render: ({ dpsAlertDelay }) => {
    if (!dpsAlertDelay) {
      return '-';
    }

    return convertSecondsToLargestUnit(dpsAlertDelay / 1000).displayedValue.toLowerCase();
  },
});

export const dpsAlertIntervalColumn = model.createColumn<AccessPointDetailsFragment>({
  title: 'DPS Alert Interval',
  render: ({ dpsAlertInterval }) => {
    if (!dpsAlertInterval) {
      return '-';
    }

    return convertSecondsToLargestUnit(dpsAlertInterval / 1000).displayedValue.toLowerCase();
  },
});

export const archivedByUserColumn = model.createColumn<AccessPointDetailsFragment>({
  title: 'Archived By',
  render: ({ archivedByUser }) => UserModel.routes.renderRowLink(archivedByUser),
});

// DataChangeLog table columns

export const mapLocationChangedColumn = model.createColumn<any>({
  title: 'Property Map Location',
  render: () => 'Updated',
});

export const archivedAtChangedColumn = model.createColumn<any>({
  title: 'Archived Status',
  render: ({ archivedAt }) => {
    if (archivedAt) {
      return <Tag style={{ fontWeight: 'bold'}}>ARCHIVED</Tag>;
    }

    return null;
  },
});
